import { DeepPartial } from "@easypost/api";
import { genericMemo } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useSearch } from "@redotech/react-util/search";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { TextMatchExpression } from "@redotech/redo-model/advanced-flow/expressions/text-expression";
import { AdvancedFlowTokenType } from "@redotech/redo-model/advanced-flow/type-system/data-types/base-types";
import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { CustomerGroupType } from "@redotech/redo-model/customers/customer-group-definition";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";

const searcher = new Fuse([], { keys: ["value"], threshold: 0.3 });

export const TokenInspector = genericMemo(function TokenInspector({
  schema,
  dataType,
  expression,
  setExpression,
}: {
  schema: Schema;
  dataType: "Token";
  expression: DeepPartial<TextMatchExpression>;
  setExpression: (expression: DeepPartial<TextMatchExpression>) => void;
}) {
  useEffect(() => {
    if (!expression.type) {
      setExpression({ ...expression, type: "text_match", operator: "equals" });
    }
  }, [expression, setExpression]);

  const [searchText, setSearchText] = useState<string>("");
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);

  const tokenOptionsLoad = useLoad(async (): Promise<
    BadgeRedoListItem<string>[]
  > => {
    if (!expression.field) {
      return [];
    }
    const field = schema.fields[expression.field];
    if (field?.dataType !== "Token") {
      return [];
    }
    switch (field.tokenType) {
      case AdvancedFlowTokenType.CUSTOMER_GROUP_ID: {
        const result = await marketingRpcClient.getCustomerGroupsMinimal({
          type: CustomerGroupType.DYNAMIC,
        });
        return result.map((segment) => ({
          type: "badge",
          id: segment._id,
          value: segment.name,
          badge: { text: segment.name },
        }));
      }
      default:
        return [
          {
            type: "badge",
            id: "1",
            value: "1",
            badge: { text: "Unsupported tokenType", color: "error" },
          },
        ];
    }
  }, [marketingRpcClient]);
  const tokenOptions = useMemo(
    () => tokenOptionsLoad.value ?? [],
    [tokenOptionsLoad.value],
  );

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!expression.matchValues) {
      return [];
    }
    return (
      tokenOptionsLoad.value?.filter((option) =>
        expression.matchValues?.includes(option.id?.toString()),
      ) ?? []
    );
  }, [expression.matchValues, tokenOptionsLoad.value]);

  const searchResults = useSearch(searcher, tokenOptions, searchText);

  return (
    <RedoTagsDropdownInput<string>
      label="Value"
      loading={tokenOptionsLoad.pending}
      options={searchResults}
      searchString={searchText}
      selectedOptions={selectedOptions}
      setSearchString={setSearchText}
      setSelectedOptions={(selectedOptions) => {
        setExpression({
          ...expression,
          matchValues: selectedOptions.map((option) => option.id?.toString()),
        });
      }}
      size="xs"
    />
  );
});
